import JSON5 from "json5";

import { dirname } from "@tsmono/util";

import { UpdateStateMessage } from "../client/api/types";

import { UrlLogSource } from "./urlLogSource";

/**
 * Single-file mode is set when the viewer is opened against a specific log
 * rather than a directory listing — e.g. an embedded iframe deep-link. We
 * need to know this before the first render so that AppRouter renders the
 * log view directly instead of mounting LogsPanel, which would otherwise
 * kick off directory-wide replication for every log in the directory.
 *
 * Takes the already-parsed invocation source so `resolveAppConfig()` parses the
 * URL exactly once.
 */
export const detectInitialSingleFileMode = (
  source: UrlLogSource,
  doc: Pick<Document, "getElementById">
): boolean => {
  if (doc.getElementById("logview-state")) {
    return true;
  }

  // This could be `none` in view server mode, or in static mode with no
  // additional param.
  return source.kind === "file";
};

/**
 * The embedded (VS Code) startup state the host injects as a `#logview-state`
 * script tag. The host emits it only for file launches (a selected `log_file`),
 * so when it's present `url` is always set. Parsed synchronously so the log root
 * is available before first render instead of arriving via a post-mount host
 * message.
 */
export const readEmbeddedStartupState = (
  doc: Pick<Document, "getElementById"> = document
): UpdateStateMessage["data"] | null => {
  const el = doc.getElementById("logview-state");
  return el
    ? JSON5.parse<UpdateStateMessage["data"]>(el.textContent || "")
    : null;
};

/**
 * Resolve a single-file reference to its directory, against the page when the
 * ref is a bare basename. Always defined: a relative ref resolves against
 * `document.baseURI` (the folder serving the page — the same base static-http
 * uses), so `dirname` is never empty.
 */
const pageBaseDir = (fileRef: string): string =>
  dirname(new URL(fileRef, document.baseURI).href);

/**
 * The log dir for a single-file session. In single-file mode we don't ask the
 * server for the log root (which would walk the whole directory); we resolve it
 * from the file itself: its own directory if it has one, else the backend's
 * configured dir (a bootstrap probe — no api instance exists yet), else the
 * page folder. Always defined — never the dishonest empty-string sentinel.
 */
export const resolveSingleFileLogDir = async (
  fileRef: string,
  resolveConfiguredDir?: () => Promise<string | undefined>
): Promise<string> => {
  const own = dirname(fileRef);
  if (own !== "") return own;
  const fromBackend = await resolveConfiguredDir?.();
  if (fromBackend) return fromBackend;
  return pageBaseDir(fileRef);
};

/** Synchronous resolution for the embedded/VS Code seed (URLs are absolute). */
export const resolveEmbeddedLogDir = (fileRef: string): string => {
  const own = dirname(fileRef);
  return own !== "" ? own : pageBaseDir(fileRef);
};
